import { api } from './api';

/**
 * TURNING ON A NOTIFICATION THAT REACHES A PHONE WHEN EDUVORA IS CLOSED.
 *
 * ⚠ THIS IS WEB PUSH, NOT A MOBILE APP. It goes through the service worker the product already
 * ships, so there is no app store, no review and no separate build. On Android and on a desktop it
 * starts working the moment somebody allows it.
 *
 * ⚠ ON AN IPHONE IT ONLY WORKS AFTER "ADD TO HOME SCREEN". Safari will not grant notification
 * permission to a page in a tab — the site has to be installed first (iOS 16.4 and later). Every
 * message in here is written on the assumption that the person reading it is holding an iPhone and
 * wondering why the switch will not turn on, because that is the support question this will
 * generate.
 */

export interface PushDevices {
  count: number;
  devices: { label: string; since: string; lastDelivered: string | null }[];
}

export type PushState =
  | 'unsupported'        // no service worker or no Push API at all
  | 'needs-install'      // iOS Safari in a tab: install to the home screen first
  | 'blocked'            // the person said no, and only they can undo it
  | 'off'
  | 'on'
  | 'unconfigured';      // the server has no VAPID key, so nothing can be sent

const pushApi = {
  key: () => api.get<{ configured: boolean; publicKey: string | null }>('/api/push/key'),
  devices: () => api.get<PushDevices>('/api/push/devices'),
  test: () => api.post<{ reached: number }>('/api/push/test'),
};

/** ⚠ Standalone display mode is how a PWA knows it was launched from the home screen. */
function isInstalled(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches === true
    || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

function isAppleTouch(): boolean {
  // ⚠ iPadOS reports itself as a Mac. The touch-point check is what separates an iPad from a
  // desktop Safari, which does support push in a tab.
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export async function currentPushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    // On an iPhone the Push API is simply absent until the app is installed, so this is the branch
    // that fires — and "your browser does not support notifications" would be a lie that leaves
    // somebody stuck. Name the actual next step instead.
    return isAppleTouch() && !isInstalled() ? 'needs-install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'blocked';

  const { configured } = await pushApi.key().catch(() => ({ configured: false }));
  if (!configured) return 'unconfigured';

  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

/**
 * Asks permission, subscribes with the server's key, and registers the device.
 *
 * ⚠ Must be called from a click. Browsers refuse a permission prompt that was not provoked by the
 * person, and the refusal looks identical to them saying no.
 */
export async function enablePush(): Promise<{ ok: boolean; message: string }> {
  const state = await currentPushState();
  if (state === 'needs-install') {
    return { ok: false, message: 'On an iPhone, first use Share → Add to Home Screen, then open Eduvora from the home screen and try again.' };
  }
  if (state === 'unsupported') {
    return { ok: false, message: 'This browser cannot show notifications.' };
  }
  if (state === 'blocked') {
    return { ok: false, message: 'Notifications are blocked for this site in your browser settings — Eduvora cannot turn them back on for you.' };
  }

  const { configured, publicKey } = await pushApi.key();
  if (!configured || !publicKey) {
    return { ok: false, message: 'Notifications are not switched on for this school yet.' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, message: 'You did not allow notifications.' };

  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    // ⚠ Required, and it must be true. A browser will reject a silent subscription outright; every
    // push has to be shown to the person.
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  const raw = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!raw.endpoint || !raw.keys?.p256dh || !raw.keys?.auth) {
    return { ok: false, message: 'The browser produced an incomplete subscription. Try again.' };
  }

  await api.post('/api/push/subscribe', {
    endpoint: raw.endpoint,
    p256dh: raw.keys.p256dh,
    auth: raw.keys.auth,
    deviceLabel: deviceLabel(),
  });

  return { ok: true, message: 'Notifications are on for this device.' };
}

export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  // ⚠ Tell the server BEFORE unsubscribing locally. The other order loses the endpoint and leaves
  // a row on the server that keeps being sent to until it fails ten times.
  await api.post('/api/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}

export async function sendTestPush(): Promise<number> {
  const { reached } = await pushApi.test();
  return reached;
}

export { pushApi };

/** A short human label so somebody can tell their phone from the staffroom computer. */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  const platform =
    /iPhone/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) ? 'iPad'
    : /Android/.test(ua) ? 'Android phone'
    : /Windows/.test(ua) ? 'Windows PC'
    : /Macintosh/.test(ua) ? 'Mac'
    : 'Browser';
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : '';
  return browser ? `${platform} · ${browser}` : platform;
}

/**
 * ⚠ The subscribe call wants raw bytes, not the base64url string the server sends.
 *
 * ⚠ Backed by an explicitly constructed ArrayBuffer. `new Uint8Array(n)` widens to
 * `Uint8Array<ArrayBufferLike>` under current lib.dom typings, which no longer satisfies
 * BufferSource because it could in principle be a SharedArrayBuffer.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
